import { useCallback } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import type { AdminUserSort, DepartmentSort, SortDirection } from '~/api/types';
import { USER_ROLES } from '~/features/auth/roles';
import { AdminUsersScreen } from '~/features/userAdministration/users/AdminUsersScreen';
import { ADMIN_USERS_TABS } from '~/features/userAdministration/users/constants';
import { UserAdminUsersSearchParamsProvider } from '~/features/userAdministration/users/params';
import type {
  UserAdminUsersSearchParams,
  UserAdminUsersSearchParamsHandler,
} from '~/features/userAdministration/users/types';
import { protectedRoute } from '~/utils/routesAuthorization';

export const Route = createFileRoute('/user-administration/users')({
  component: RouteComponent,
  validateSearch: (search): UserAdminUsersSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;
    const filters = search.filters ?? undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort:
        (search.sort as AdminUserSort | DepartmentSort | undefined) ??
        undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
      filters,
      tab:
        (search.tab as UserAdminUsersSearchParams['tab']) ??
        ADMIN_USERS_TABS.all,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.HcpAdmin, USER_ROLES.NonHcpAdmin],
    });
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const onSearchParamsChange: UserAdminUsersSearchParamsHandler = useCallback(
    (onSearch) => navigate({ search: onSearch }),
    [navigate],
  );

  return (
    <UserAdminUsersSearchParamsProvider
      params={Route.useSearch()}
      onSearchParamsChange={onSearchParamsChange}
    >
      <AdminUsersScreen>
        <Outlet />
      </AdminUsersScreen>
    </UserAdminUsersSearchParamsProvider>
  );
}
